import type { GreenScore } from "@/types/public-data"

const SIZE_CONFIG = {
  sm: { dimension: 80, strokeWidth: 6, fontSize: "text-lg", subFontSize: "text-[10px]" },
  md: { dimension: 120, strokeWidth: 8, fontSize: "text-3xl", subFontSize: "text-xs" },
  lg: { dimension: 160, strokeWidth: 10, fontSize: "text-4xl", subFontSize: "text-sm" },
} as const

function scoreColour(score: number): string {
  if (score >= 70) return "#1A1A1A" // charcoal
  if (score >= 40) return "#3A3A3C" // slate
  return "#C41E2A" // red
}

const BREAKDOWN_LABELS: Record<string, string> = {
  greenSpace: "Green space",
  floodRisk: "Flood risk",
  conservation: "Conservation",
  walkability: "Walkability",
  cycling: "Cycling",
}

interface GreenScoreBadgeProps {
  score: GreenScore
  size?: "sm" | "md" | "lg"
}

export function GreenScoreBadge({ score, size = "md" }: GreenScoreBadgeProps) {
  const config = SIZE_CONFIG[size]
  const radius = (config.dimension - config.strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const progress = Math.min(Math.max(score.total, 0), 100)
  const offset = circumference - (progress / 100) * circumference
  const centre = config.dimension / 2

  return (
    <div className="inline-flex flex-col items-center gap-1">
      <div className="relative" style={{ width: config.dimension, height: config.dimension }}>
        <svg
          width={config.dimension}
          height={config.dimension}
          viewBox={`0 0 ${config.dimension} ${config.dimension}`}
          className="block"
        >
          {/* Background ring */}
          <circle
            cx={centre}
            cy={centre}
            r={radius}
            fill="none"
            stroke="#E8E4DF"
            strokeWidth={config.strokeWidth}
          />
          {/* Score ring */}
          <circle
            cx={centre}
            cy={centre}
            r={radius}
            fill="none"
            stroke={scoreColour(score.total)}
            strokeWidth={config.strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${centre} ${centre})`}
            className="transition-[stroke-dashoffset] duration-700 ease-out"
          />
        </svg>
        {/* Centered text overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-display font-bold text-charcoal ${config.fontSize}`}>
            {score.total}
          </span>
          <span className={`text-slate ${config.subFontSize}`}>/ 100</span>
        </div>
      </div>
      <span className="text-xs font-medium text-slate">Green Living Score</span>
    </div>
  )
}

interface GreenScoreBreakdownProps {
  score: GreenScore
}

export function GreenScoreBreakdown({ score }: GreenScoreBreakdownProps) {
  const entries = Object.entries(score.breakdown) as [string, number][]

  return (
    <div className="space-y-3">
      {entries.map(([key, value]) => (
        <div key={key}>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-sm text-slate">
              {BREAKDOWN_LABELS[key] ?? key}
            </span>
            <span className="text-sm font-medium text-charcoal">{value}</span>
          </div>
          <div className="h-2 w-full rounded-full bg-stone">
            <div
              className="h-2 rounded-full transition-all duration-500 ease-out"
              style={{
                width: `${value}%`,
                backgroundColor: scoreColour(value),
              }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}
